import React from "react";

import { getHashParam, setHashParam } from "../../lib/hashState";
import { useOrganization } from "@clerk/clerk-react";

import { Select } from "../../design";
import { humanize } from "../../lib/format";
import type { OrgBilling, OrgTier, SeatTier } from "../../lib/types";

// Canonical tier order + display names.
export const SEAT_TIER_ORDER: SeatTier[] = ["t5", "t20", "t50"];

// Seat tiers this org can buy: the ones billing prices, in canonical order.
export function seatTiers(billing: OrgBilling | null): SeatTier[] {
  const priced = billing?.tiers?.map((t) => t.tier);
  if (!priced?.length) return SEAT_TIER_ORDER;
  return SEAT_TIER_ORDER.filter((t) => priced.includes(t));
}

export function seatCounts(): Record<SeatTier, number> {
  return { t5: 0, t20: 0, t50: 0 };
}

// Monthly cost of every purchased seat at its tier's price.
export function seatsMonthlyTotal(billing: OrgBilling | null): number {
  if (!billing) return 0;
  return seatTiers(billing).reduce(
    (acc, t) =>
      acc + (billing.seat_tiers?.[t] ?? 0) * tierMeta(t, billing).price,
    0,
  );
}

export const TIER_NAME: Record<string, string> = {
  t5: "Starter",
  t20: "Pro",
  t50: "Max",
};

export function tierName(tier: string | null | undefined): string {
  if (!tier) return "None";
  return TIER_NAME[tier] ?? tier;
}

export type Identity = { name: string; email: string };

/** One page section at a time, chosen by tabs and kept in the URL (?tab=). */
export function usePanel<T extends string>(
  keys: readonly T[],
  fallback: T,
): [T, (k: T) => void] {
  const [panel, setPanel] = React.useState<T>(() => {
    const v = getHashParam("tab") as T | null;
    return v && keys.includes(v) ? v : fallback;
  });
  const go = (k: T) => {
    setPanel(k);
    setHashParam("tab", k === fallback ? null : k);
  };
  React.useEffect(() => {
    const sync = () => {
      const v = getHashParam("tab") as T | null;
      setPanel(v && keys.includes(v) ? v : fallback);
    };
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [keys, fallback]);
  return [panel, go];
}

// Paginate the active Clerk org's memberships into a clerk_user_id → identity
// map. The backend member rows carry email but not display name.
export function useIdentities(): Map<string, Identity> {
  const { organization } = useOrganization();
  const [identities, setIdentities] = React.useState<Map<string, Identity>>(
    new Map(),
  );

  React.useEffect(() => {
    if (!organization) return;
    let cancelled = false;
    (async () => {
      const map = new Map<string, Identity>();
      let initialPage = 1;
      const pageSize = 100;
      for (;;) {
        const page = await organization.getMemberships({
          initialPage,
          pageSize,
        });
        for (const m of page.data) {
          const uid = m.publicUserData?.userId;
          if (!uid) continue;
          const name =
            [m.publicUserData?.firstName, m.publicUserData?.lastName]
              .filter(Boolean)
              .join(" ") ||
            (m.publicUserData?.identifier ?? "");
          map.set(uid, { name, email: m.publicUserData?.identifier ?? "" });
        }
        if (page.data.length < pageSize) break;
        initialPage += 1;
      }
      if (!cancelled) setIdentities(map);
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [organization]);

  return identities;
}

export function SeatPill({
  value,
  onPick,
  tiers = SEAT_TIER_ORDER,
  busy = false,
  disabled = false,
  label = "Seat tier",
  quiet,
  className,
}: {
  value: SeatTier | null;
  onPick: (tier: SeatTier | null) => void;
  tiers?: SeatTier[];
  busy?: boolean;
  disabled?: boolean;
  label?: string;
  quiet?: boolean;
  className?: string;
}) {
  return (
    <Select
      size="sm"
      quiet={quiet}
      className={className}
      ariaLabel={label}
      value={value ?? "none"}
      options={[
        { value: "none", label: "None" },
        ...tiers.map((t) => ({ value: t, label: tierName(t) })),
      ]}
      disabled={busy || disabled}
      onChange={(v) => {
        const next = v === "none" ? null : v;
        if (next !== value) onPick(next);
      }}
    />
  );
}

export function tierMeta(
  tier: SeatTier,
  billing: OrgBilling | null,
): { price: number; tokens: string } {
  const fromBilling = billing?.tiers?.find((t: OrgTier) => t.tier === tier);
  if (fromBilling) {
    return {
      price: fromBilling.price_usd,
      tokens: humanize(fromBilling.week_cap),
    };
  }
  const fallback: Record<SeatTier, { price: number; tokens: string }> = {
    t5: { price: 5, tokens: "500M" },
    t20: { price: 20, tokens: "2.5B" },
    t50: { price: 50, tokens: "10B" },
  };
  return fallback[tier];
}

const CREATE_EVENT = "helm:create";

/** The page header's create button asks the section below to open its form,
 * so every create action can live in the header slot. */
export function requestCreate(what: string) {
  window.dispatchEvent(new CustomEvent(CREATE_EVENT, { detail: what }));
}

export function useCreateRequest(what: string, run: () => void) {
  const latest = React.useRef(run);
  latest.current = run;
  React.useEffect(() => {
    const on = (e: Event) => {
      if ((e as CustomEvent).detail === what) latest.current();
    };
    window.addEventListener(CREATE_EVENT, on);
    return () => window.removeEventListener(CREATE_EVENT, on);
  }, [what]);
}
